import { useCallback, useEffect, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import {
  capabilitiesView,
  type CapabilitiesResponse,
  type CapabilitiesView,
  fetchAiCapabilities,
  saveCapabilitySwitch,
  withSwitch,
} from './aiCapabilitiesApi';

export type AiCapabilitiesLoad =
  | { state: 'loading' }
  | { state: 'error'; message: string }
  | { state: 'ready'; view: CapabilitiesView };

type Body = { state: 'loading' } | { state: 'error'; message: string } | { state: 'ready'; body: CapabilitiesResponse };

/**
 * GET /api/ai/capabilities, again whenever `retry` is called; and `toggle`, which
 * switches one capability on or off (PUT /api/ai/capabilities/:capability). While
 * a save is on its way the switch shows its new state and `saving` is true; on
 * success the page shows what the server saved, and on failure the switch goes
 * back and `saveError` says why.
 */
export function useAiCapabilities(): {
  load: AiCapabilitiesLoad;
  retry: () => void;
  toggle: (key: string, enabled: boolean) => void;
  saving: boolean;
  saveError: string | null;
} {
  const [loaded, setLoaded] = useState<Body>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoaded({ state: 'loading' });
    fetchAiCapabilities(authenticatedFetch, { signal: controller.signal }).then(
      (body) => setLoaded({ state: 'ready', body }),
      (error: unknown) => {
        if (!controller.signal.aborted) {
          setLoaded({ state: 'error', message: error instanceof Error ? error.message : 'Could not load AI capabilities.' });
        }
      },
    );
    return () => controller.abort();
  }, [attempt]);

  const toggle = useCallback(
    (key: string, enabled: boolean) => {
      if (loaded.state !== 'ready' || saving) return;
      const before = loaded.body;
      setSaving(true);
      setSaveError(null);
      setLoaded({ state: 'ready', body: withSwitch(before, key, enabled) });
      saveCapabilitySwitch(authenticatedFetch, key, enabled).then(
        (saved) => setLoaded({ state: 'ready', body: saved }),
        (error: unknown) => {
          setLoaded({ state: 'ready', body: before });
          setSaveError(error instanceof Error ? error.message : 'Try again.');
        },
      ).finally(() => setSaving(false));
    },
    [loaded, saving],
  );

  const load: AiCapabilitiesLoad =
    loaded.state === 'ready' ? { state: 'ready', view: capabilitiesView(loaded.body) } : loaded;
  return { load, retry: () => setAttempt((n) => n + 1), toggle, saving, saveError };
}
